import { useState, type ReactNode } from 'react';
import { useT } from '../i18n';
import '../styles/screens.css';

export type DisplayNameError = 'required' | 'too_short' | 'too_long';

const DISPLAY_NAME_ERROR_KEYS: Record<DisplayNameError, string> = {
  required: 'displayName.errorRequired',
  too_short: 'displayName.errorTooShort',
  too_long: 'displayName.errorTooLong',
};

interface DisplayNameInputProps {
  value: string;
  onChange: (name: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Validates a display name. Returns an error code (not a translated message).
 * Components map the code to a translated message via the i18n system.
 */
export function validateDisplayName(name: string): DisplayNameError | null {
  const trimmed = name.trim();
  if (trimmed.length === 0) return 'required';
  if (trimmed.length < 2) return 'too_short';
  if (trimmed.length > 20) return 'too_long';
  return null;
}

export function DisplayNameInput({
  value,
  onChange,
  placeholder,
  disabled = false,
}: DisplayNameInputProps): ReactNode {
  const t = useT();
  const [touched, setTouched] = useState(false);
  const errorCode = touched ? validateDisplayName(value) : null;
  const error = errorCode ? t(DISPLAY_NAME_ERROR_KEYS[errorCode] as any) : null;

  return (
    <div>
      <label className="field-label" htmlFor="display-name">
        {t('displayName.label')}
      </label>
      <input
        id="display-name"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setTouched(true)}
        placeholder={placeholder ?? t('displayName.placeholder')}
        disabled={disabled}
        className={'field-input' + (error ? ' field-input--error' : '')}
        maxLength={20}
        autoComplete="off"
      />
      {error && <p className="field-hint field-hint--error">{error}</p>}
    </div>
  );
}
